import { Download } from "lucide-react";
import { ORG } from "@/data/tools";
import { useActiveSection } from "@/hooks/useStoredState";
import { printTools, scrollToId } from "@/lib/scroll";
import { LogoMark } from "@/components/Reveal";
import { NumberCtx } from "@/components/workbook/numbering";

export const FullFooter = () => (
  <p className="text-xs leading-snug print:text-[10px]">
    {ORG.name} · SEND Family Hub · {ORG.web} · {ORG.director} · {ORG.datalead} · ICO Registration: {ORG.ico} · Company Number: {ORG.company} · Supporting schools, families and professionals through practical SEND resources, toolkits and insights.
  </p>
);

const Cover = ({ eyebrow, title, subtitle, lines }) => (
  <div className="print-cover">
    <LogoMark className="h-24 w-24" />
    <p className="mt-10 text-lg font-bold tracking-[0.2em]">THE SEND ACCOUNTABILITY PROJECT</p>
    <p className="mt-2 font-bold tracking-[0.2em]">{eyebrow}</p>
    <h1 className="mt-8 font-display text-6xl font-medium leading-[1]">{title}</h1>
    <p className="mt-6 text-xl">{subtitle}</p>
    {lines.map((l) => <p key={l} className="mt-2 text-lg">{l}</p>)}
    <p className="mt-10 text-lg"><strong>{ORG.name}</strong><br />Supporting schools, families and professionals through practical SEND resources, toolkits and insights.</p>
  </div>
);

const Index = ({ sections, label }) => {
  const active = useActiveSection(sections.map((s) => s.id));
  return (
    <nav aria-label={`Sections in ${label}`} className="no-print sticky top-36 hidden max-h-[calc(100vh-10rem)] overflow-y-auto lg:block" data-lenis-prevent>
      <p className="eyebrow mb-4">In this {label}</p>
      <ol className="space-y-0.5 border-l-2 border-line">
        {sections.map((t) => (
          <li key={t.id}>
            <a href={`#${t.id}`} aria-current={active === t.id ? "true" : undefined} data-testid={`pack-index-${t.id}`}
              onClick={(e) => { e.preventDefault(); scrollToId(t.id); }}
              className={`-ml-0.5 flex gap-3 border-l-2 py-1.5 pl-4 text-sm font-bold transition-colors ${active === t.id ? "border-brand text-brand" : "border-transparent text-ink-soft hover:text-ink"}`}>
              <span className="w-5 shrink-0 font-display italic">{t.n || "—"}</span>{t.title}
            </a>
          </li>
        ))}
      </ol>
      <button type="button" onClick={() => printTools(null)} className="btn btn-brand btn-sm mt-6" data-testid="pack-print-side">
        <Download size={14} aria-hidden /> Print or save the whole {label}
      </button>
    </nav>
  );
};

/** Top-bar index strip built from a pack's sections, so phones get section jumps too. */
export const packIndex = (label, sections) => ({
  label: `${label} index`,
  items: sections.map((s) => ({ id: s.id, n: s.n, short: s.title })),
  ids: sections.map((s) => s.id),
});

/** A fill-in pack: sticky index, every section in order, one print with cover and branded footer. */
export const PackPage = ({ packId, label, cover, sections }) => (
  <section id={packId} data-pack={packId} aria-label={label} className="print-scope border-y border-line bg-surface py-16 lg:py-24">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-10">
      <div className="pack-grid grid gap-12 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div><Index sections={sections} label={label} /></div>
        <table className="print-table">
          <tfoot><tr><td><FullFooter /></td></tr></tfoot>
          <tbody><tr><td>
            <Cover {...cover} />
            <NumberCtx.Provider value={Object.fromEntries(sections.map((s) => [s.id, s.n]))}>
              <div className="space-y-10">{sections.map(({ id, El, n }) => <El key={id} n={n} />)}</div>
            </NumberCtx.Provider>
          </td></tr></tbody>
        </table>
      </div>
    </div>
  </section>
);
